import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, HelpCircle, PlusCircle, History, LogOut, Settings, ShieldCheck, Users } from 'lucide-react';
import { authService } from '../services/api';

export const Sidebar = () => {
  const location = useLocation();
  const user = authService.getCurrentUser();

  const menuItems = [
    { path: '/dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
    { path: '/questionnaires', label: 'Questionnaires', icon: FileText },
    { path: '/questions', label: 'Questions', icon: HelpCircle },
    { path: '/generer', label: 'Générer', icon: PlusCircle },
    { path: '/candidats', label: 'Candidats', icon: Users },
    { path: '/historique', label: 'Historique', icon: History },
  ];

  const handleLogout = async () => {
    await authService.logout();
  };

  const NavLink = ({ path, label, icon: Icon }) => {
    const isActive = location.pathname === path;
    return (
      <Link
        to={path}
        data-testid={`nav-${path.slice(1)}`}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 transition-all duration-200 ${
          isActive
            ? 'bg-gradient-to-r from-blue-600/20 to-indigo-500/10 text-white border border-blue-500/20'
            : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'
        }`}
      >
        <Icon size={18} strokeWidth={1.5} />
        <span className="text-sm font-medium">{label}</span>
      </Link>
    );
  };

  return (
    <div className="eva-sidebar" data-testid="sidebar" style={{ background: '#0B132B', borderRight: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="p-5 border-b border-white/[0.06]">
        <h1 className="text-2xl font-bold text-white font-heading tracking-tight">
          Eva
        </h1>
        <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-widest">by ASAP Technologies</p>
      </div>

      <nav className="p-3 flex-1">
        {menuItems.map((item) => (
          <NavLink key={item.path} {...item} />
        ))}
      </nav>

      <div className="border-t border-white/[0.06] px-3 pt-2 pb-1">
        {user?.role === 'admin' ? (
          <NavLink path="/admin" label="Administration" icon={ShieldCheck} />
        ) : (
          <NavLink path="/parametres" label="Paramètres" icon={Settings} />
        )}
        {user?.role === 'admin' && (
          <NavLink path="/parametres" label="Paramètres" icon={Settings} />
        )}
      </div>

      <div className="px-4 pb-4 border-t border-white/[0.06] pt-3">
        {user && (
          <div className="mb-3 px-1">
            <div className="flex items-center gap-2">
              <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Connecté</p>
              {user.role === 'admin' && (
                <span className="text-[9px] px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded-full font-mono font-medium">Admin</span>
              )}
            </div>
            <p className="text-sm font-medium text-white mt-1">{user.prenom} {user.nom}</p>
            <p className="text-xs text-slate-500">{user.email}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
        >
          <LogOut size={16} />
          Déconnexion
        </button>
      </div>
    </div>
  );
};

export const Header = ({ title, breadcrumbs }) => {
  const user = authService.getCurrentUser();

  return (
    <div className="flex justify-between items-center px-8 py-5 border-b border-white/[0.06]" data-testid="header" style={{ background: '#0B132B' }}>
      <div>
        {breadcrumbs && (
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span className="text-slate-600">/</span>}
                <span>{crumb}</span>
              </React.Fragment>
            ))}
          </div>
        )}
        <h2 className="text-2xl font-bold tracking-tight text-white font-heading">{title}</h2>
      </div>
      
      {user && (
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-sm font-medium text-white">{user.prenom} {user.nom}</p>
            <p className="text-xs text-slate-500">{user.email}</p>
          </div>
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-500 rounded-full flex items-center justify-center shadow-lg shadow-blue-900/30">
            <span className="text-white font-semibold text-sm">
              {user.prenom?.[0]}{user.nom?.[0]}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export const Layout = ({ children, title, breadcrumbs }) => {
  return (
    <div className="eva-container">
      <Sidebar />
      <div className="eva-main-content" style={{ background: '#0B132B' }}>
        <Header title={title} breadcrumbs={breadcrumbs} />
        <div className="p-8" style={{ background: '#0B132B' }}>{children}</div>
      </div>
    </div>
  );
};
